import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Building2, CheckCircle, Loader2, Calendar, ClipboardList, Sparkles, Battery, AlertTriangle, WifiOff } from "lucide-react";
import { GeminiHeader } from "@/components/GeminiHeader";
import { motion } from "framer-motion";
import { SmartCoworkerPicker } from "@/components/SmartCoworkerPicker";
import { FatigueContextBanner } from "@/components/FatigueContextBanner";
import { useOfflineStatus } from "@/hooks/useOfflineStatus";
import { savePendingCheckin, generateOfflineId } from "@/utils/offlineStorage";

const PreShiftCheckin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isOffline } = useOfflineStatus();
  const [profile, setProfile] = useState<any>(null);
  const [coworkers, setCoworkers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [formData, setFormData] = useState({
    checkInDate: new Date().toISOString().split('T')[0],
    shiftName: '',
    workLocation: '',
    workAreaType: '',
    plannedTasksText: '',
    workingWith: [] as string[],
    gpsCoordinates: '',
    gpsAccuracy: 0,
    fatigueScore: 0 // Samn-Perelli Fatigue Scale (1-7)
  });

  // Samn-Perelli Fatigue Scale levels
  const fatigueScale = [{
    value: 1,
    label: 'Segar Bugar',
    emoji: '💪',
    color: 'bg-emerald-500',
    description: 'Siap kerja 100%'
  }, {
    value: 2,
    label: 'Sangat Fit',
    emoji: '😊',
    color: 'bg-emerald-400',
    description: 'Energi tinggi'
  }, {
    value: 3,
    label: 'Cukup Fit',
    emoji: '🙂',
    color: 'bg-lime-400',
    description: 'Normal'
  }, {
    value: 4,
    label: 'Agak Lelah',
    emoji: '😐',
    color: 'bg-yellow-400',
    description: 'Sedikit capek'
  }, {
    value: 5,
    label: 'Lelah',
    emoji: '😓',
    color: 'bg-orange-400',
    description: 'Butuh istirahat'
  }, {
    value: 6,
    label: 'Sangat Lelah',
    emoji: '😩',
    color: 'bg-orange-500',
    description: 'Kelelahan tinggi'
  }, {
    value: 7,
    label: 'Kelelahan',
    emoji: '😵',
    color: 'bg-red-500',
    description: 'Sangat capek'
  }];
  useEffect(() => {
    loadProfile();
    checkTodayCheckin();
    captureGPS();
  }, []);
  const loadProfile = async () => {
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) {
      navigate('/signup');
      return;
    }
    const {
      data: profileData
    } = await supabase.from('worker_profiles').select('*').eq('user_id', session.user.id).single();
    if (profileData) {
      setProfile(profileData);
      loadCoworkers(profileData.company_id, profileData.id, profileData.site_id);
    }
  };
  const loadCoworkers = async (companyId: string, currentWorkerId: string, siteId?: string) => {
    let query = supabase.from('worker_profiles').select('id, display_name, team').eq('company_id', companyId).neq('id', currentWorkerId);
    if (siteId) {
      query = query.eq('site_id', siteId);
    }
    const {
      data
    } = await query.limit(50);
    if (data) {
      setCoworkers(data);
    }
  };
  const checkTodayCheckin = async () => {
    const today = new Date().toISOString().split('T')[0];
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) return;
    const {
      data: profileData
    } = await supabase.from('worker_profiles').select('id').eq('user_id', session.user.id).single();
    if (!profileData) return;
    const {
      data
    } = await supabase.from('shift_checkins').select('*').eq('worker_id', profileData.id).eq('check_in_date', today).maybeSingle();
    if (data) {
      setIsCheckedIn(true);
      setFormData({
        checkInDate: data.check_in_date,
        shiftName: data.shift_name,
        workLocation: data.work_location,
        workAreaType: data.work_area_type || '',
        plannedTasksText: Array.isArray(data.planned_tasks) ? data.planned_tasks.join(', ') : '',
        workingWith: data.working_with || [],
        gpsCoordinates: data.gps_coordinates || '',
        gpsAccuracy: data.gps_accuracy || 0,
        fatigueScore: data.fatigue_score || 0
      });
    }
  };
  const captureGPS = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(position => {
        const coords = `${position.coords.latitude},${position.coords.longitude}`;
        setFormData(prev => ({
          ...prev,
          gpsCoordinates: coords,
          gpsAccuracy: position.coords.accuracy
        }));
      }, error => {
        console.error('GPS error:', error);
      });
    }
  };
  const toggleCoworker = (workerId: string) => {
    setFormData(prev => ({
      ...prev,
      workingWith: prev.workingWith.includes(workerId) ? prev.workingWith.filter(w => w !== workerId) : [...prev.workingWith, workerId]
    }));
  };
  const handleCheckin = async () => {
    if (!formData.shiftName || !formData.workLocation || !formData.plannedTasksText.trim()) {
      toast({
        title: "Data tidak lengkap",
        description: "Mohon isi shift, lokasi, dan tugas hari ini",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    
    const plannedTasksArray = formData.plannedTasksText.split(',').map(t => t.trim()).filter(t => t);
    
    const checkinData = {
      worker_id: profile.id,
      site_id: profile.site_id,
      check_in_date: formData.checkInDate,
      shift_name: formData.shiftName,
      work_location: formData.workLocation,
      work_area_type: formData.workAreaType,
      planned_tasks: plannedTasksArray,
      working_with: formData.workingWith,
      gps_coordinates: formData.gpsCoordinates,
      gps_accuracy: formData.gpsAccuracy,
      fatigue_score: formData.fatigueScore > 0 ? formData.fatigueScore : null,
      position_data: {
        timestamp: new Date().toISOString(),
        device: navigator.userAgent
      }
    };
    
    try {
      // If offline, save to IndexedDB
      if (isOffline) {
        const offlineId = generateOfflineId();
        await savePendingCheckin({
          id: offlineId,
          data: checkinData,
          createdAt: new Date().toISOString(),
          retryCount: 0,
        });
        
        // Cache the check-in data locally for offline access
        localStorage.setItem('sorin_cache_last_checkin_' + profile.id, JSON.stringify({
          data: { work_location: formData.workLocation, shift_name: formData.shiftName, planned_tasks: plannedTasksArray },
          timestamp: Date.now()
        }));
        
        setIsCheckedIn(true);
        toast({
          title: "Check-in Tersimpan Offline",
          description: "Akan disinkronkan saat koneksi kembali",
        });
        setTimeout(() => navigate('/home'), 2000);
        return;
      }
      
      // Online - normal flow
      const { error } = await supabase.from('shift_checkins').insert(checkinData);
      if (error) throw error;
      
      try {
        await supabase.functions.invoke('process-checkin-connections', {
          body: {
            workerId: profile.id,
            companyId: profile.company_id,
            siteId: profile.site_id,
            shiftName: formData.shiftName,
            workLocation: formData.workLocation,
            plannedTasksText: formData.plannedTasksText,
            workingWith: formData.workingWith,
            checkInDate: formData.checkInDate,
            fatigueScore: formData.fatigueScore
          }
        });
      } catch (connError) {
        console.error('Connection processing error:', connError);
      }
      
      // Calculate fatigue risk after check-in
      supabase.functions.invoke('calculate-fatigue-risk', {
        body: {
          workerId: profile.id,
          companyId: profile.company_id,
          siteId: profile.site_id,
          fatigueScore: formData.fatigueScore,
          shiftName: formData.shiftName
        }
      }).catch(err => console.error('Fatigue risk calculation error:', err));
      
      supabase.functions.invoke('predict-risk-score', {
        body: {
          workerId: profile.id,
          fatigueScore: formData.fatigueScore
        }
      }).catch(err => console.error('Risk recalc error:', err));
      setIsCheckedIn(true);

      // Show fatigue warning toast if score >= 5
      if (formData.fatigueScore >= 5) {
        toast({
          title: "⚠️ Tingkat Kelelahan Tinggi",
          description: "Kamu terlihat lelah. Pastikan istirahat cukup, hindari tugas berat, dan hati-hati dengan mesin berbahaya.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Check-in berhasil!",
          description: "Selamat bekerja dengan aman"
        });
      }
      setTimeout(() => navigate('/home'), formData.fatigueScore >= 5 ? 4000 : 2000);
    } catch (error: any) {
      console.error('Check-in error:', error);
      
      // If network error, save offline
      const isNetworkError = 
        error.message?.includes('fetch') ||
        error.message?.includes('network') ||
        error.message?.includes('Failed to fetch');
      
      if (isNetworkError) {
        const offlineId = generateOfflineId();
        await savePendingCheckin({
          id: offlineId,
          data: checkinData,
          createdAt: new Date().toISOString(),
          retryCount: 0,
        });
        
        setIsCheckedIn(true);
        toast({
          title: "Koneksi Terputus",
          description: "Check-in disimpan offline dan akan disinkronkan nanti",
        });
        setTimeout(() => navigate('/home'), 2000);
        return;
      }
      
      toast({
        title: "Check-in gagal",
        description: "Silakan coba lagi",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  if (!profile) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>;
  }
  return <div className="min-h-screen bg-background">
      <GeminiHeader title="Check-in Shift" subtitle="Catat kehadiran & rencana kerja" backPath="/home" />
      
      <main className="max-w-2xl mx-auto px-4 py-6 pb-24">
        {isCheckedIn ? <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="text-center py-12">
            {/* Success Card */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 p-8 mb-6">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: "spring",
            duration: 0.5,
            delay: 0.1
          }} className="h-20 w-20 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
              </motion.div>
              <h3 className="text-2xl font-semibold mb-2">Sudah Check-in!</h3>
              <p className="text-muted-foreground">
                {formData.shiftName === 'morning' ? 'Shift Pagi' : formData.shiftName === 'afternoon' ? 'Shift Siang' : 'Shift Malam'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{formData.workLocation}</p>
            </motion.div>
            
            <Button onClick={() => navigate('/home')} className="h-12 px-8 rounded-full">
              Lanjut ke Home
            </Button>
          </motion.div> : <div className="space-y-6">
            {/* Hero Banner */}
            

            {/* Quick Info Cards */}
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05
        }} className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-muted/40 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Tanggal</span>
                </div>
                <p className="font-medium text-sm">
                  {new Date(formData.checkInDate).toLocaleDateString('id-ID', {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              })}
                </p>
              </div>
              
              {formData.gpsCoordinates && <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-xs text-emerald-600 dark:text-emerald-400">GPS Aktif</span>
                  </div>
                  <p className="font-medium text-sm text-emerald-700 dark:text-emerald-300">
                    ±{Math.round(formData.gpsAccuracy)}m akurasi
                  </p>
                </div>}
            </motion.div>

            {/* Form Section */}
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="space-y-5">
              <h3 className="font-semibold text-lg">Detail Shift</h3>
              
              {/* Shift Selection */}
              <div className="rounded-2xl bg-muted/40 p-4 space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Pilih Shift</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{
                  value: 'morning',
                  label: 'Pagi',
                  time: '07-15'
                }, {
                  value: 'afternoon',
                  label: 'Siang',
                  time: '15-23'
                }, {
                  value: 'night',
                  label: 'Malam',
                  time: '23-07'
                }].map(shift => <button key={shift.value} onClick={() => setFormData(prev => ({
                  ...prev,
                  shiftName: shift.value
                }))} className={`p-3 rounded-xl text-center transition-all ${formData.shiftName === shift.value ? 'bg-primary text-primary-foreground shadow-md' : 'bg-background hover:bg-muted border border-border/50'}`}>
                        <div className="font-medium text-sm">{shift.label}</div>
                        <div className={`text-xs mt-0.5 ${formData.shiftName === shift.value ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                          {shift.time}
                        </div>
                      </button>)}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Lokasi Kerja</label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Contoh: Workshop A, Gudang B" value={formData.workLocation} onChange={e => setFormData(prev => ({
                  ...prev,
                  workLocation: e.target.value
                }))} className="h-12 pl-11 rounded-xl bg-background border-border/50" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Tasks Card */}
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15
        }} className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-8 w-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                  <ClipboardList className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Rencana Kerja</h4>
                  <p className="text-xs text-muted-foreground">Apa yang akan kamu kerjakan?</p>
                </div>
              </div>
              <Textarea placeholder="Contoh: Pengelasan pipa, pemasangan kabel listrik, bongkar muat material..." value={formData.plannedTasksText} onChange={e => setFormData(prev => ({
            ...prev,
            plannedTasksText: e.target.value
          }))} className="min-h-[100px] rounded-xl border-0 bg-white/80 dark:bg-background/80 resize-none text-base" />
              <div className="flex items-center gap-1.5 mt-2">
                <Sparkles className="h-3 w-3 text-blue-500" />
                <p className="text-xs text-muted-foreground">
                  AI akan menganalisis risiko berdasarkan tugas ini
                </p>
              </div>
            </motion.div>

            {/* Fatigue Scale Card - Samn-Perelli */}
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className={`rounded-2xl p-4 ${formData.fatigueScore >= 5 ? 'bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 border border-orange-200 dark:border-orange-800' : 'bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30'}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${formData.fatigueScore >= 5 ? 'bg-orange-100 dark:bg-orange-900/50' : 'bg-cyan-100 dark:bg-cyan-900/50'}`}>
                  <Battery className={`h-4 w-4 ${formData.fatigueScore >= 5 ? 'text-orange-600 dark:text-orange-400' : 'text-cyan-600 dark:text-cyan-400'}`} />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Tingkat Energi</h4>
                  <p className="text-xs text-muted-foreground">Bagaimana kondisi fisikmu saat ini?</p>
                </div>
              </div>
              
              <div className="grid grid-cols-7 gap-1.5">
                {fatigueScale.map(level => <button key={level.value} onClick={() => setFormData(prev => ({
              ...prev,
              fatigueScore: level.value
            }))} className={`p-2 rounded-xl text-center transition-all flex flex-col items-center ${formData.fatigueScore === level.value ? `${level.color} text-white shadow-md scale-105` : 'bg-background hover:bg-muted border border-border/50'}`}>
                    <span className="text-lg">{level.emoji}</span>
                    <span className="text-[10px] font-medium mt-0.5">{level.value}</span>
                  </button>)}
              </div>
              
              {formData.fatigueScore > 0 && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-3">
                  <div className={`flex items-center gap-2 p-3 rounded-xl ${formData.fatigueScore >= 5 ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-white/80 dark:bg-background/80'}`}>
                    <span className="text-2xl">{fatigueScale.find(f => f.value === formData.fatigueScore)?.emoji}</span>
                    <div>
                      <p className="font-medium text-sm">{fatigueScale.find(f => f.value === formData.fatigueScore)?.label}</p>
                      <p className="text-xs text-muted-foreground">{fatigueScale.find(f => f.value === formData.fatigueScore)?.description}</p>
                    </div>
                  </div>
                  
                  {formData.fatigueScore >= 5 && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex items-start gap-2 mt-2 p-3 rounded-xl bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800">
                      <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-orange-700 dark:text-orange-300">
                        Tingkat kelelahan tinggi terdeteksi. Hindari tugas berisiko tinggi dan pastikan istirahat cukup.
                      </p>
                    </motion.div>}
                </motion.div>}
            </motion.div>

            {/* Smart Coworkers Picker */}
            {profile && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <SmartCoworkerPicker
                  companyId={profile.company_id}
                  currentWorkerId={profile.id}
                  siteId={profile.site_id}
                  selectedIds={formData.workingWith}
                  onSelectionChange={(ids) => setFormData(prev => ({ ...prev, workingWith: ids }))}
                />
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="pt-2">
              <Button onClick={handleCheckin} disabled={isLoading || !formData.shiftName || !formData.workLocation || !formData.plannedTasksText.trim()} className="w-full h-14 rounded-2xl text-base font-medium bg-gradient-to-r from-primary to-primary/90 hover:opacity-95 shadow-lg shadow-primary/25" size="lg">
                {isLoading ? <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Memproses...
                  </> : <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Check-in Sekarang
                  </>}
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-3">
                Check-in akan mencatat lokasi & waktu otomatis
              </p>
            </motion.div>
          </div>}
      </main>
    </div>;
};
export default PreShiftCheckin;